import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Download, Loader2, FileText } from 'lucide-react';
import { useGetDocumentDownloadUrl } from '@/hooks/useDocuments';
import { isInlinePreviewable, resolveFileMimeType } from '@/utils/fileMime';

export type FilePreviewSource =
  | { type: 'document'; documentId: string; filename: string; mimeType: string }
  | { type: 'local'; file: File };

interface FilePreviewOverlayProps {
  source: FilePreviewSource;
  onClose: () => void;
}

export function FilePreviewOverlay({ source, onClose }: FilePreviewOverlayProps) {
  const { t } = useTranslation();
  const { mutateAsync: fetchDocumentUrl } = useGetDocumentDownloadUrl();
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const filename = source.type === 'document' ? source.filename : source.file.name;
  const mimeType =
    source.type === 'document' ? source.mimeType : resolveFileMimeType(source.file);
  const canPreview = isInlinePreviewable(mimeType);

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(false);
      setUrl(null);

      try {
        if (source.type === 'local') {
          objectUrl = URL.createObjectURL(source.file);
          if (!cancelled) setUrl(objectUrl);
        } else {
          const signed = await fetchDocumentUrl(source.documentId);
          if (!cancelled) setUrl(signed);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [source, fetchDocumentUrl]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleDownload = () => {
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/85">
      <div
        className="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-4 py-3"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="min-w-0 truncate text-sm font-medium text-white">{filename}</p>
        <div className="flex shrink-0 items-center gap-2">
          {url && (
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-3 py-1.5 text-xs text-white hover:bg-white/10"
            >
              <Download className="h-3.5 w-3.5" />
              {t('documents.download')}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            title={t('common.close')}
            className="rounded-lg p-1.5 text-white/70 hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center overflow-auto p-4" onClick={onClose}>
        <div
          className="flex max-h-full max-w-full items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {loading && <Loader2 className="h-8 w-8 animate-spin text-white/70" />}

          {error && (
            <p className="text-sm text-white/80">{t('documents.preview_failed')}</p>
          )}

          {url && !loading && !error && !canPreview && (
            <div className="flex max-w-md flex-col items-center gap-4 px-6 text-center">
              <FileText className="h-12 w-12 text-white/50" />
              <p className="text-sm text-white/80">{t('documents.no_preview')}</p>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 px-4 py-2 text-sm text-white hover:bg-white/10"
              >
                <Download className="h-4 w-4" />
                {t('documents.download')}
              </button>
            </div>
          )}

          {url && !loading && !error && mimeType.startsWith('image/') && (
            <img
              src={url}
              alt={filename}
              className="max-h-[calc(100vh-5rem)] max-w-[min(100vw-2rem,64rem)] object-contain"
            />
          )}

          {url && !loading && !error && mimeType.startsWith('video/') && (
            <video
              src={url}
              controls
              className="max-h-[calc(100vh-5rem)] max-w-[min(100vw-2rem,64rem)] rounded-lg bg-black"
            />
          )}

          {url && !loading && !error && mimeType === 'application/pdf' && (
            <iframe
              src={url}
              title={filename}
              className="h-[calc(100vh-5rem)] w-[min(100vw-2rem,64rem)] rounded-lg bg-white"
            />
          )}

          {url && !loading && !error && mimeType === 'text/plain' && (
            <iframe
              src={url}
              title={filename}
              className="h-[calc(100vh-5rem)] w-[min(100vw-2rem,64rem)] rounded-lg bg-white"
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
